import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

const isRunningOnClient =
  typeof window !== 'undefined' && window.document ? true : false;
// NOTE: google-maps does stuff with window and is not safe for server side rendering. It's only imported client side (webpack converts commonjs import to something else that works in the browser).
const GoogleMapsLoader = isRunningOnClient ? require('google-maps') : false;

import mapOptions, {
  lightStylesWithDetails,
  darkStylesWithDetails,
  minimalStyles
} from './map-options';

import Heading from 'components/heading';
import ContentWrapper from 'components/content-wrapper';

class Map extends React.Component {
  static propTypes = {
    title: PropTypes.string,
    googleMapsAPIKey: PropTypes.string,
    htmlId: PropTypes.string,
    markers: PropTypes.arrayOf(
      PropTypes.exact({
        name: PropTypes.string,
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
        infoboxContent: PropTypes.string
      })
    ),
    showDetails: PropTypes.bool,
    theme: PropTypes.string
  };

  static propTypesMeta = {
    markers: [
      {
        latitude: 'float',
        longitude: 'float'
      }
    ]
  };

  static defaultProps = {
    markers: [],
    theme: 'light'
  };

  google = null;
  map = null;
  mapElement = null;
  infoWindow = null;

  getBounds = markers => {
    const google = this.google;
    const bounds = new google.maps.LatLngBounds();

    if (markers && markers.length) {
      markers.forEach(marker => {
        bounds.extend(
          new google.maps.LatLng(marker.latitude, marker.longitude)
        );
      });
    }

    return bounds;
  };

  fitMapToMarkers = markers => {
    const bounds = this.getBounds(markers);

    if (markers.length === 1) {
      this.map.setCenter(bounds.getCenter());
      this.map.setZoom(16);
    } else {
      this.map.fitBounds(bounds);
    }
  };

  componentDidMount() {
    GoogleMapsLoader.KEY = this.props.googleMapsAPIKey;
    GoogleMapsLoader.VERSION = '3.36';
    GoogleMapsLoader.LIBRARIES = ['geometry', 'places'];
    GoogleMapsLoader.load(google => {
      this.google = google;

      const styleMap =
        this.props.theme === 'minimal'
          ? minimalStyles
          : this.props.theme === 'dark'
            ? darkStylesWithDetails
            : lightStylesWithDetails;

      this.map = new google.maps.Map(this.mapElement, {
        //mapId: 'DEMO_MAP_ID',
        ...mapOptions,
        styles:
          this.props.theme === 'minimal'
            ? minimalStyles
            : styleMap.map(style => {
              if (
                (style.featureType === 'transit' ||
                  style.featureType === 'poi') &&
                style.elementType === 'labels'
              ) {
                return {
                  ...style,
                  stylers: [
                    { visibility: this.props.showDetails ? 'on' : 'off' }
                  ]
                };
              }
              return style;
            })
      });

      this.fitMapToMarkers(this.props.markers);
      this.infoWindow = new google.maps.InfoWindow();
      this.props.markers.forEach(async marker => {
        /* 
        // If MapId is set, and want to use AdvancedMarker
        const newMarker = new google.maps.marker.AdvancedMarkerElement({
          position: {
            lat: marker.latitude,
            lng: marker.longitude
          },
          title: marker.name,
          map: this.map
        });

        return newMarker; */
        const newMarker = new google.maps.Marker({
          position: {
            lat: marker.latitude,
            lng: marker.longitude
          },
          optimized: marker.infoboxContent ? false : true,
          title: marker.name,
          map: this.map,
          icon: {
            path:
              'M13.7705 0C10.1196 0.00414168 6.61943 1.45629 4.03786 4.03786C1.45629 6.61943 0.00414168 10.1196 0 13.7705C0 25.5537 12.5186 34.4528 13.0522 34.8253C13.2627 34.9727 13.5135 35.0518 13.7705 35.0518C14.0275 35.0518 14.2783 34.9727 14.4887 34.8253C15.0224 34.4528 27.541 25.5537 27.541 13.7705C27.5368 10.1196 26.0847 6.61943 23.5031 4.03786C20.9216 1.45629 17.4214 0.00414168 13.7705 0ZM13.7705 8.76304C14.7609 8.76304 15.729 9.05672 16.5525 9.60695C17.376 10.1572 18.0178 10.9392 18.3968 11.8542C18.7758 12.7692 18.8749 13.776 18.6817 14.7474C18.4885 15.7187 18.0116 16.611 17.3113 17.3113C16.611 18.0116 15.7187 18.4885 14.7474 18.6817C13.776 18.8749 12.7692 18.7758 11.8542 18.3968C10.9392 18.0178 10.1572 17.376 9.60695 16.5525C9.05672 15.729 8.76304 14.7609 8.76304 13.7705C8.76304 12.4424 9.29061 11.1688 10.2297 10.2297C11.1688 9.29061 12.4424 8.76304 13.7705 8.76304Z',
            fillColor: '#BE3C40',
            fillOpacity: 1,
            scale: 1,
            strokeColor: '#BE3C40',
            strokeOpacity: 1,
            strokeWeight: 1,
            anchor: new google.maps.Point(13.7705, 35.0518)
          }
        });

        marker.infoboxContent &&
          newMarker.addListener("click", () => {
            this.infoWindow.close();
            this.infoWindow.setContent(marker.infoboxContent);
            this.infoWindow.open(newMarker.getMap(), newMarker);
          });

        return {
          marker: newMarker
        };
      });
    });
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.showDetails !== this.props.showDetails &&
      this.map &&
      this.google
    ) {
      const styleMap =
        this.props.theme === 'minimal'
          ? minimalStyles
          : this.props.theme === 'dark'
            ? darkStylesWithDetails
            : lightStylesWithDetails;

      const styles =
        this.props.theme === 'minimal'
          ? minimalStyles
          : styleMap.map(style => {
            if (
              (style.featureType === 'transit' ||
                style.featureType === 'poi') &&
              style.elementType === 'labels'
            ) {
              return {
                ...style,
                stylers: [
                  { visibility: this.props.showDetails ? 'on' : 'off' }
                ]
              };
            }
            return style;
          });

      this.map.setOptions({ styles });
    }
  }

  render() {
    return (
      <>
        {this.props.title && (
          <ContentWrapper className="map-title">
            <Heading level={2} theme={Heading.themes.h2}>
              {this.props.title}
            </Heading>
          </ContentWrapper>
        )}
        <div
          className={cn('map', {
            'map--minimal': this.props.theme === 'minimal'
          })}
          id={this.props.htmlId}
          ref={div => (this.mapElement = div)}
        />
      </>
    );
  }
}

export default Map;
